import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';

/**
 * @typedef {Object} CardGridItem
 * @property {string} title - Card title
 * @property {string} [description] - Card body text (omit for title-only cards)
 * @property {React.ReactNode} [icon] - Card icon
 */

/**
 * @typedef {Object} CardGridSectionProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [ariaLabel] - Accessible label for the section
 * @property {string} [eyebrow] - Small label above the headline
 * @property {string} headline - Section headline
 * @property {string} [subheadline] - Section subheadline
 * @property {CardGridItem[]} items - Cards to render
 * @property {number} [columns] - Number of columns on desktop (default: 3)
 * @property {string} [backgroundColor] - Section background color
 */

/**
 * Section header followed by a responsive grid of cards.
 *
 * @param {CardGridSectionProps} props - Component props
 * @returns {JSX.Element} Card grid section
 */
function CardGridSection({
  sectionId,
  ariaLabel,
  eyebrow,
  headline,
  subheadline,
  items,
  columns = 3,
  backgroundColor,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label={ariaLabel || headline}
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: backgroundColor || theme.palette.background.paper,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
          {eyebrow && (
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: '#5e7b08',
                fontWeight: 600,
                letterSpacing: '0.1em',
                mb: 1,
              }}
            >
              {eyebrow}
            </Typography>
          )}
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            {headline}
          </Heading>
          {subheadline && (
            <Text
              align="center"
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: '#6c757d',
                maxWidth: '650px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              {subheadline}
            </Text>
          )}
        </Box>

        {/* Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: `repeat(${columns}, 1fr)`,
            },
            gap: 3,
          }}
        >
          {items.map((item, index) => (
            <Box
              key={item.title}
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: '20px',
                p: { xs: 3, md: 4 },
                border: '1px solid rgba(0, 0, 0, 0.06)',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
                textAlign: item.description ? 'left' : 'center',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.6s ease',
                transitionDelay: `${index * 100}ms`,
              }}
            >
              {item.icon && (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '14px',
                    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    mx: item.description ? 0 : 'auto',
                  }}
                >
                  {item.icon}
                </Box>
              )}
              <Typography
                variant="h5"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  fontSize: { xs: '1.2rem', md: '1.35rem' },
                  mb: item.description ? 1.5 : 0,
                }}
              >
                {item.title}
              </Typography>
              {item.description && (
                <Typography
                  variant="body1"
                  sx={{ color: '#4a4a4a', lineHeight: 1.7 }}
                >
                  {item.description}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default CardGridSection;
